<script setup lang="ts">
import type { ApiHeader, ApiHeaderAlert } from '~~/server/api/header/index'
import type { AlertColors } from '~/components/alert/Alert.vue'
import infoIcon from '~/assets/icons/info-icon.svg'
import warningIcon from '~/assets/icons/warning-icon.svg'
import successIcon from '~/assets/icons/success-icon.svg'
import errorIcon from '~/assets/icons/error-icon.svg'

const route = useRoute()
const searchRoute = useNamedRoutes({ name: 'search' })
const { data: header } = useLazyFetch<ApiHeader>('/api/header')

const alerts = computed<ApiHeaderAlert[]>(() => {
    return header.value?.data?.[route.path] ?? []
})

const alertWrapper = ref<HTMLDivElement | null>(null)
const alertRef = ref<HTMLDivElement[] | null>(null)

const alertColorMap: Record<ApiHeaderAlert['type'], AlertColors> = {
    success: 'success',
    error: 'error',
    info: 'info',
    warning: 'warning',
}

const alertIconMap: Record<ApiHeaderAlert['type'], string> = {
    info: infoIcon,
    warning: warningIcon,
    success: successIcon,
    error: errorIcon,
}

watch(() => alertRef.value?.length, (newVal) => {
    if(!newVal) {
        document?.documentElement.style.removeProperty('--alert-height')
        return
    }
    document?.documentElement.style.setProperty('--alert-height', `calc(${alertWrapper.value?.offsetHeight || 0}px + 2.3rem)`)
}, { immediate: true, deep: true })
</script>
<template>
  <div
    v-if="alerts.length"
    ref="alertWrapper"
    class="container-fluid mt-2"
  >
    <Alert
      v-for="(alert, index) in alerts"
      :key="index"
      :color="alertColorMap[alert.type]"
      class="mb-3"
      variant="light"
      dismissible
      show-icon
      style="justify-content: start !important;"
    >
      <div
        ref="alertRef"
        class="d-flex"
      >
        <img
          :src="alertIconMap[alert.type]"
          alt="Alert icon"
          width="16"
          height="16"
          class="flex-shrink-0 me-2"
        >
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="fs-normal"
          v-html="alert.text"
        />
      </div>
    </Alert>
  </div>

  <IconsWrapper>
    <IconsSymbolCrossSmall />
  </IconsWrapper>
  
  <header class="header p-3 py-md-4 ps-md-0 pe-md-0 bg-white z-index-20">
    <Tracker :data="{ position: 'Page' }">
      <ContainerFluid class="h-100">
        <div
          class="d-flex align-items-start justify-content-center justify-content-md-between h-100 mb-md-4"
        >
          <HeaderLogo />
          <SearchAutocomplete
            v-if="searchRoute !== route.path"
            :show-suggestions="true"
          />
          <MenuToggle />
          <div class="d-none d-md-flex flex-shrink-0 align-items-center pt-14 pt-xl-0">
            <MenuReportDamageWizardButton />
            <Tracker :data="{ title: $t('Klientsky portál') }">
              <MenuClientPortalLink />
            </Tracker>
          </div>
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <Menu />
          <MenuButtons />
        </div>
      </ContainerFluid>
    </Tracker>
  </header>
</template>

<style>
@import './Header.vars.scss';
</style>
<style scoped lang="scss">
@import './Header.scss';
</style>
